import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Badge, EditArticleButton, StatusDot } from '@/components/atoms';
import { formatarNumero, formatarDataHora } from '@/i18n/formato';
import { useArticles, useCategories } from '@/hooks/useArticles';
import { destinationsApi } from '@/services/config';
import { editoriasApi, type Mode } from '@/services/editorias';
import { cartoesDaGestao, contarDestinos, type CartaoGestao } from '@/utils/gestao';

import styles from './Gestao.module.css';

function isoToday(): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

function isoNDaysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

const TOM_DO_MODO: Record<Mode, 'ok' | 'info' | 'neutral'> = {
  auto: 'ok',
  semi: 'info',
  off: 'neutral',
};

interface CartaoDaEditoriaProps {
  cartao: CartaoGestao;
  nomeDoSite: string | null;
}

function CartaoDaEditoria({ cartao, nomeDoSite }: CartaoDaEditoriaProps) {
  const { t } = useTranslation(['articles', 'common']);
  const { editoria, site, categoriaId } = cartao;
  // Sem site ou sem categoria não há o que contar: as consultas nem saem.
  const consulta = site !== null && categoriaId !== null;
  const base = { site: site ?? undefined, categories: categoriaId !== null ? [categoriaId] : [] };

  // `per_page: 1`: a tela só lê o `total`; trazer 100 matérias para contar era desperdício.
  const hoje = useArticles({ ...base, after: isoToday(), per_page: 1 }, { enabled: consulta });
  const semana = useArticles(
    { ...base, after: isoNDaysAgo(7), per_page: 1 },
    { enabled: consulta },
  );
  const recentes = useArticles({ ...base, per_page: 5 }, { enabled: consulta });
  const { data: categorias } = useCategories(site ?? undefined);

  const itens = recentes.data?.items ?? [];
  const noArquivo = categorias?.find((c) => c.id === categoriaId)?.count;
  const fonte = editoria.feed.fonte || editoria.feed.kind;

  return (
    <article
      className={styles.card}
      aria-label={`${editoria.nome} em ${nomeDoSite ?? 'sem destino'}`}
    >
      <header className={styles.cardHead}>
        <div>
          <h2 className={styles.cardTitle}>{editoria.nome}</h2>
          <p className={styles.muted}>
            {fonte}
            {editoria.feed.intervalo ? ` · ${editoria.feed.intervalo}` : ''}
          </p>
        </div>
        <div className={styles.badges}>
          <Badge tone={TOM_DO_MODO[editoria.modo]} size="xs">
            {t(`articles:gestao.modo.${editoria.modo}`)}
          </Badge>
          <Badge tone={site ? 'info' : 'warn'} size="xs">
            {site ? (nomeDoSite ?? site) : t('articles:gestao.sem_destino')}
          </Badge>
          {editoria.pub.categoria && (
            <Badge tone="neutral" size="xs">
              {editoria.pub.categoria}
            </Badge>
          )}
        </div>
      </header>

      {!consulta ? (
        <p className={styles.muted}>
          {site === null
            ? t('articles:gestao.sem_destino_desc')
            : t('articles:gestao.sem_categoria')}
        </p>
      ) : (
        <>
          <div className={styles.metrics}>
            <div className={styles.metric}>
              <span className={styles.metricNumber}>{hoje.data?.total ?? 0}</span>
              <span className={styles.metricLabel}>{t('articles:gestao.hoje')}</span>
            </div>
            <div className={styles.metric}>
              <span className={styles.metricNumber}>{semana.data?.total ?? 0}</span>
              <span className={styles.metricLabel}>{t('articles:gestao.sete_dias')}</span>
            </div>
            {noArquivo !== undefined && (
              <div className={styles.metric}>
                <span className={styles.metricNumber}>{formatarNumero(noArquivo)}</span>
                <span className={styles.metricLabel}>{t('articles:gestao.arquivo')}</span>
              </div>
            )}
          </div>

          <section>
            <p className={styles.sectionTitle}>{t('articles:gestao.latest_published')}</p>
            {itens.length === 0 && <p className={styles.muted}>{t('articles:gestao.sem_dados')}</p>}
            <ul className={styles.recentList}>
              {itens.map((article) => (
                <li key={article.id} className={styles.recentRow}>
                  <StatusDot state="ok" />
                  <Link
                    to={`/artigos/${article.id}?site=${article.site}`}
                    className={styles.recentLink}
                  >
                    {article.title}
                  </Link>
                  <span className={styles.recentTime}>
                    {formatarDataHora(article.published_at, {
                      day: '2-digit',
                      month: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                  <EditArticleButton id={article.id} size="sm" variant="ghost" />
                </li>
              ))}
            </ul>
          </section>
        </>
      )}
    </article>
  );
}

function UltimasPublicacoes() {
  const { t } = useTranslation(['articles', 'common']);
  const recentes = useArticles({ per_page: 8 });
  const itens = recentes.data?.items ?? [];
  return (
    <article className={styles.cardWide}>
      <header className={styles.cardHead}>
        <div>
          <h2 className={styles.cardTitle}>{t('articles:gestao.ultimas_title')}</h2>
          <p className={styles.muted}>{t('articles:gestao.ultimas_desc')}</p>
        </div>
      </header>
      {itens.length === 0 && <p className={styles.muted}>{t('articles:gestao.sem_dados')}</p>}
      <ul className={styles.recentList}>
        {itens.map((article) => (
          <li key={article.id} className={styles.recentRow}>
            <StatusDot state="ok" />
            <Link to={`/artigos/${article.id}?site=${article.site}`} className={styles.recentLink}>
              {article.title}
            </Link>
            <span className={styles.recentTime}>
              {formatarDataHora(article.published_at, {
                day: '2-digit',
                month: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </span>
            <EditArticleButton id={article.id} size="sm" variant="ghost" />
          </li>
        ))}
      </ul>
    </article>
  );
}

export function Gestao() {
  const { t } = useTranslation(['articles', 'common']);
  const editorias = useQuery({ queryKey: ['editorias'], queryFn: editoriasApi.list });
  const sites = useQuery({
    queryKey: ['wp-sites', 'active'],
    queryFn: () => destinationsApi.list(),
    staleTime: 5 * 60_000,
  });

  const cartoes = useMemo(() => cartoesDaGestao(editorias.data ?? []), [editorias.data]);
  const nomes = useMemo(
    () => new Map((sites.data ?? []).map((s) => [s.slug, s.name])),
    [sites.data],
  );
  const totalEditorias = editorias.data?.length ?? 0;

  return (
    <section>
      <header className={styles.head}>
        <h1 className={styles.title}>{t('articles:gestao.title')}</h1>
        {editorias.data && (
          <Badge tone="info" size="sm">
            {t('articles:gestao.resumo', {
              editorias: totalEditorias,
              destinos: contarDestinos(cartoes),
            })}
          </Badge>
        )}
      </header>

      <h2 className={styles.sectionTitle}>{t('articles:gestao.editorias')}</h2>
      {editorias.isLoading && <p className={styles.muted}>{t('articles:gestao.carregando')}</p>}
      {editorias.isError && (
        <p className={styles.muted} role="alert">
          {t('articles:gestao.erro')}
        </p>
      )}
      {editorias.data && totalEditorias === 0 && (
        <p className={styles.muted}>
          {t('articles:gestao.sem_editorias')}{' '}
          <Link to="/editorias">{t('articles:gestao.abrir_editorias')}</Link>
        </p>
      )}
      <div className={styles.grid}>
        {cartoes.map((c) => (
          <CartaoDaEditoria
            key={c.chave}
            cartao={c}
            nomeDoSite={c.site ? (nomes.get(c.site) ?? c.site) : null}
          />
        ))}
      </div>

      <div style={{ marginTop: 'var(--sp-6)' }}>
        <UltimasPublicacoes />
      </div>
    </section>
  );
}
